import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, CalendarBlank } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PTDatePicker from '@/components/PTDatePicker';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const API = process.env.REACT_APP_BACKEND_URL;
const CAT_OPTIONS = ['alimentação', 'hidratação', 'comportamento', 'estilo de vida', 'infantil'];

/**
 * PROMPT 5 — extraído de PacienteDetailPage. Tab admin para gerir metas comportamentais
 * de uma paciente. Comportamento e data-testids preservados verbatim.
 */
export default function MetasAdminTab({ patientId }) {
  const confirmDelete = useConfirmDelete();
  const [goals, setGoals] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'alimentação', description: '', target_type: 'binary', target_value: '', start_date: '', end_date: '' });
  // TASK 2: editar datas de uma meta existente — dialog separado
  const [editingDates, setEditingDates] = useState(null); // { id, start_date, end_date }
  const fetch = useCallback(async () => {
    try {
      const [g, l] = await Promise.all([
        axios.get(`${API}/api/patients/${patientId}/goals-list`),
        axios.get(`${API}/api/patients/${patientId}/goal-logs`),
      ]);
      setGoals(g.data); setLogs(l.data);
    } catch {}
  }, [patientId]);
  useEffect(() => { fetch(); }, [fetch]);
  const createGoal = async () => {
    try { await axios.post(`${API}/api/patients/${patientId}/goals-list`, { ...form, target_value: form.target_value || null, start_date: form.start_date || undefined, end_date: form.end_date || null }); toast.success('Meta criada'); setShowAdd(false); setForm({ title: '', category: 'alimentação', description: '', target_type: 'binary', target_value: '', start_date: '', end_date: '' }); fetch(); } catch { toast.error('Erro'); }
  };
  const toggleGoal = async (goalId, active) => {
    await axios.put(`${API}/api/goals/${goalId}`, { active: !active }); fetch();
  };
  const deleteGoal = async (goalId) => {
    const g = goals.find(x => x.id === goalId);
    const ok = await confirmDelete({ description: `Vai apagar a meta "${g?.title || ''}".` });
    if (!ok) return;
    await axios.delete(`${API}/api/goals/${goalId}`); toast.success('Meta eliminada'); fetch();
  };
  // TASK 2: guarda as novas datas para uma meta existente
  const saveDates = async () => {
    if (!editingDates) return;
    try {
      await axios.put(`${API}/api/goals/${editingDates.id}`, {
        start_date: editingDates.start_date || null,
        end_date: editingDates.end_date || null,
      });
      toast.success('Datas atualizadas');
      setEditingDates(null);
      fetch();
    } catch { toast.error('Erro a guardar datas'); }
  };
  // TASK a (Fase 2): fim não pode ser anterior ao início
  const datesInverted = (start, end) => !!start && !!end && end < start;
  const editDatesInvalid = editingDates && datesInverted(editingDates.start_date, editingDates.end_date);
  const newDatesInvalid = datesInverted(form.start_date, form.end_date);
  // Weekly compliance per goal
  const getWeeklyPct = (goalId) => {
    const from = new Date(); from.setDate(from.getDate() - 7);
    const fromStr = from.toISOString().split('T')[0];
    const gLogs = logs.filter(l => l.goal_id === goalId && l.date >= fromStr);
    const done = gLogs.filter(l => l.status === 'done' || l.status === 'partial').length;
    return Math.round(done / 7 * 100);
  };
  return (
    <div data-testid="tab-metas">
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowAdd(true)} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-1"><Plus size={14} /> Nova meta</button>
      </div>
      {goals.length === 0 ? <p className="text-center text-brand-muted py-8">Sem metas definidas.</p> : (
        <div className="space-y-2">
          {goals.map(g => {
            const wpct = getWeeklyPct(g.id);
            return (
              <div key={g.id} className={`bg-white border rounded-2xl p-3 ${g.active ? 'border-brand-border' : 'border-brand-border/50 opacity-60'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-brand-text">{g.title}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      {g.challenge_label && <span className="text-[9px] bg-brand-gold/15 text-brand-gold-hover px-1.5 py-0.5 rounded-full font-medium" data-testid={`goal-challenge-${g.id}`}>{g.challenge_label}</span>}
                      <span className="text-[9px] bg-brand-mint/15 text-brand-green px-1.5 py-0.5 rounded-full">{g.category}</span>
                      {g.target_type === 'quantitative' && <span className="text-[10px] text-brand-muted">Obj: {g.target_value}</span>}
                      <span className={`text-[10px] font-medium ${wpct >= 70 ? 'text-brand-green' : wpct >= 40 ? 'text-brand-gold-hover' : 'text-brand-muted'}`}>{wpct}% semana</span>
                      {(g.start_date || g.end_date) && (
                        <button type="button" onClick={() => setEditingDates({ id: g.id, start_date: g.start_date || '', end_date: g.end_date || '' })} className="text-[9px] bg-brand-bg/60 border border-brand-border text-brand-muted px-1.5 py-0.5 rounded-full hover:border-brand-green/40 hover:text-brand-green cursor-pointer transition-colors" title="Clica para editar as datas" data-testid={`goal-range-${g.id}`}>
                          {g.start_date || '—'} → {g.end_date || '∞'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setEditingDates({ id: g.id, start_date: g.start_date || '', end_date: g.end_date || '' })} className="text-[10px] px-2 py-1 rounded-full text-brand-muted border border-brand-border hover:text-brand-green hover:border-brand-green/30 flex items-center gap-1" title="Editar intervalo de datas" data-testid={`edit-dates-${g.id}`}>
                      <CalendarBlank size={11} /> Datas
                    </button>
                    <button onClick={() => toggleGoal(g.id, g.active)} className={`text-[10px] px-2 py-1 rounded-full ${g.active ? 'text-brand-muted border border-brand-border' : 'text-brand-green border border-brand-green/30'}`}>{g.active ? 'Desativar' : 'Ativar'}</button>
                    <button onClick={() => deleteGoal(g.id)} className="p-1 text-brand-placeholder hover:text-red-500"><Trash size={13} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl border-brand-border">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Nova meta</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs text-brand-muted">Define um objetivo comportamental para a paciente, com categoria, tipo e intervalo de datas opcional.</DialogDescription>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Título</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Beber 6 copos de água" className="rounded-xl border-brand-border" required /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Categoria</Label>
              <div className="flex flex-wrap gap-1.5">{CAT_OPTIONS.map(c => <button key={c} type="button" onClick={() => setForm({ ...form, category: c })} className={`px-2.5 py-1 rounded-full text-xs ${form.category === c ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>{c}</button>)}</div>
            </div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Tipo</Label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setForm({ ...form, target_type: 'binary' })} className={`flex-1 py-2 rounded-xl text-xs font-medium ${form.target_type === 'binary' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Sim/Não</button>
                <button type="button" onClick={() => setForm({ ...form, target_type: 'quantitative' })} className={`flex-1 py-2 rounded-xl text-xs font-medium ${form.target_type === 'quantitative' ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}>Quantitativa</button>
              </div>
            </div>
            {form.target_type === 'quantitative' && <div><Label className="text-xs text-brand-muted mb-1 block">Valor alvo</Label><Input type="number" value={form.target_value} onChange={e => setForm({ ...form, target_value: e.target.value })} className="w-24 rounded-xl border-brand-border" /></div>}            {/* TASK 1: intervalo de datas via PTDatePicker (PT-PT dd/mm/yyyy). Vazio = só dia de hoje. */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Início (opcional)</Label>
                <PTDatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="goal-start-date" className="w-full" />
              </div>
              <div>
                <Label className="text-xs text-brand-muted mb-1 block">Fim (opcional)</Label>
                <PTDatePicker value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testid="goal-end-date" className="w-full" />
              </div>
            </div>
            <p className="text-[10px] text-brand-placeholder">Sem datas = a meta aparece apenas no dia de hoje. Com intervalo, a paciente pode marcar dias em atraso manualmente.</p>
            {newDatesInvalid && <p className="text-[10px] text-red-500" data-testid="new-dates-error">A data de fim não pode ser anterior à data de início.</p>}
            <div><Label className="text-xs text-brand-muted mb-1 block">Descrição (opcional)</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-xl border-brand-border" /></div>
            <button onClick={createGoal} disabled={newDatesInvalid} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-40 disabled:cursor-not-allowed" data-testid="create-goal-save">Criar meta</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* TASK 2: dialog para editar datas de uma meta existente */}
      <Dialog open={!!editingDates} onOpenChange={(o) => { if (!o) setEditingDates(null); }}>
        <DialogContent className="rounded-3xl border-brand-border max-w-sm" data-testid="edit-dates-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Editar intervalo</DialogTitle></DialogHeader>
          <DialogDescription className="text-xs text-brand-muted">Ajusta as datas de início e fim em que esta meta fica ativa para a paciente.</DialogDescription>
          {editingDates && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-brand-muted mb-1 block">Início</Label>
                  <PTDatePicker value={editingDates.start_date} onChange={(v) => setEditingDates({ ...editingDates, start_date: v })} testid="edit-start-date" className="w-full" />
                </div>
                <div>
                  <Label className="text-xs text-brand-muted mb-1 block">Fim</Label>
                  <PTDatePicker value={editingDates.end_date} onChange={(v) => setEditingDates({ ...editingDates, end_date: v })} testid="edit-end-date" className="w-full" />
                </div>
              </div>
              <p className="text-[10px] text-brand-placeholder">Limpa as duas para repor ao comportamento de dia único (só hoje).</p>
              {editDatesInvalid && <p className="text-[10px] text-red-500" data-testid="edit-dates-error">A data de fim não pode ser anterior à data de início.</p>}
              <div className="flex gap-2">
                <button onClick={() => setEditingDates(null)} className="flex-1 border border-brand-border text-brand-muted rounded-full py-2 text-sm">Cancelar</button>
                <button onClick={saveDates} disabled={editDatesInvalid} className="flex-1 bg-brand-green text-white rounded-full py-2 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed" data-testid="edit-dates-save">Guardar</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
